import { useEffect, useState, type ReactNode } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {
  Alert,
  Button,
  Collapse,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Modal,
  Radio,
  Result,
  Skeleton,
  Tag,
  Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import {
  CLIENT_DEACTIVATION_CUTOFF_STRATEGY,
  CLIENT_DEACTIVATION_CURRENT_PERIOD_TREATMENT,
  CLIENT_DEACTIVATION_ERROR_CODE,
  CLIENT_DEACTIVATION_INVOICE_STATUS,
  CLIENT_DEACTIVATION_OPERATION_STATUS,
  CLIENT_DEACTIVATION_OUTCOME,
  formatCurrency,
  type ClientDeactivationCurrentPeriod,
  type ClientDeactivationCurrentPeriodTreatment,
  type ClientDeactivationCutoffStrategy,
  type ClientDeactivationInvoiceStatus,
  type ClientDeactivationOutcome,
  type ClientDeactivationOperationStatus,
  type ClientDeactivationPreviewResponse,
  type ClientDeactivationResponse,
  type ClienteResponse,
  type PreviewClientDeactivationDto,
} from '@aid/shared';
import { useClientDeactivationPreview, useConfirmClientDeactivation } from '../api/useClientes';
import { useDebouncedValue } from '../../../lib/useDebouncedValue';
import { DATE_FORMAT, formatearPeriodo } from '../../../lib/date';
import { useT } from '../../../locales';

const { Text } = Typography;
const TODAY = () => dayjs().format('YYYY-MM-DD');

const RESULT_STATUS = {
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  INFO: 'info',
} as const;

type ResultStatus = (typeof RESULT_STATUS)[keyof typeof RESULT_STATUS];

interface ClientDeactivationModalProps {
  open: boolean;
  cliente: ClienteResponse;
  onClose: () => void;
  onSuccess?: (result: ClientDeactivationResponse) => void;
}

interface ClientDeactivationSelection {
  cutoffStrategy: ClientDeactivationCutoffStrategy;
  currentPeriodTreatment: ClientDeactivationCurrentPeriodTreatment;
  effectiveDate: string;
}

type Translate = ReturnType<typeof useT>;

function createOperationKey(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return `client-deactivation:${globalThis.crypto.randomUUID()}`;
  }
  return `client-deactivation:${Date.now()}:${Math.random().toString(36).slice(2)}`;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(DATE_FORMAT) : '—';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getErrorCode(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const responseData: unknown = error.response?.data;
  if (!isRecord(responseData)) return null;
  return typeof responseData.error === 'string' ? responseData.error : null;
}

function getErrorMessage(error: unknown, t: Translate): string {
  switch (getErrorCode(error)) {
    case CLIENT_DEACTIVATION_ERROR_CODE.PREVIEW_STALE:
      return t('pages.clientes.deactivation.error.previewStale');
    case CLIENT_DEACTIVATION_ERROR_CODE.OPERATION_KEY_REUSED:
      return t('pages.clientes.deactivation.error.operationKeyReused');
    case CLIENT_DEACTIVATION_ERROR_CODE.POLICY_INVALID:
      return t('pages.clientes.deactivation.error.policyInvalid');
    case CLIENT_DEACTIVATION_ERROR_CODE.PREPAID_SHAPE_UNRESOLVED:
      return t('pages.clientes.deactivation.error.prepaidUnresolved');
    case CLIENT_DEACTIVATION_ERROR_CODE.ALREADY_INACTIVE:
      return t('pages.clientes.deactivation.error.alreadyInactive');
    case CLIENT_DEACTIVATION_ERROR_CODE.DUE_UNRESOLVED:
      return t('pages.clientes.deactivation.error.dueUnresolved');
    default:
      return t('pages.clientes.deactivation.error.generic');
  }
}

function invoiceStatusLabel(value: ClientDeactivationInvoiceStatus, t: Translate): string {
  switch (value) {
    case CLIENT_DEACTIVATION_INVOICE_STATUS.NONE:
      return t('pages.clientes.deactivation.invoiceStatus.none');
    case CLIENT_DEACTIVATION_INVOICE_STATUS.PAID:
      return t('pages.clientes.deactivation.invoiceStatus.paid');
    case CLIENT_DEACTIVATION_INVOICE_STATUS.PARTIAL:
      return t('pages.clientes.deactivation.invoiceStatus.partial');
    case CLIENT_DEACTIVATION_INVOICE_STATUS.UNPAID:
      return t('pages.clientes.deactivation.invoiceStatus.unpaid');
    default:
      return t('pages.clientes.deactivation.invoiceStatus.unknown');
  }
}

function serviceStateLabel(value: string, t: Translate): string {
  switch (value) {
    case 'ACTIVO':
      return t('pages.clientes.deactivation.serviceState.active');
    case 'ACTIVO_AL_CORTE':
      return t('pages.clientes.deactivation.serviceState.activeAtCutoff');
    case 'SUSPENDIDO':
      return t('pages.clientes.deactivation.serviceState.suspended');
    case 'BAJA':
      return t('pages.clientes.deactivation.serviceState.inactive');
    case 'BAJA_AL_CORTE':
      return t('pages.clientes.deactivation.serviceState.inactiveAtCutoff');
    case 'PENDIENTE':
      return t('pages.clientes.deactivation.serviceState.pending');
    default:
      return t('pages.clientes.deactivation.serviceState.unknown');
  }
}

function operationStatusLabel(value: ClientDeactivationOperationStatus, t: Translate): string {
  switch (value) {
    case CLIENT_DEACTIVATION_OPERATION_STATUS.SCHEDULED:
      return t('pages.clientes.deactivation.result.status.scheduled');
    case CLIENT_DEACTIVATION_OPERATION_STATUS.APPLIED:
      return t('pages.clientes.deactivation.result.status.applied');
    case CLIENT_DEACTIVATION_OPERATION_STATUS.BLOCKED:
      return t('pages.clientes.deactivation.result.status.blocked');
    case CLIENT_DEACTIVATION_OPERATION_STATUS.REACTIVATED:
      return t('pages.clientes.deactivation.result.status.reactivated');
    default:
      return t('pages.clientes.deactivation.unknownStatus');
  }
}

function outcomeLabel(value: ClientDeactivationOutcome, t: Translate): string {
  switch (value) {
    case CLIENT_DEACTIVATION_OUTCOME.SCHEDULED:
      return t('pages.clientes.deactivation.result.outcome.scheduled');
    case CLIENT_DEACTIVATION_OUTCOME.APPLIED:
      return t('pages.clientes.deactivation.result.outcome.applied');
    case CLIENT_DEACTIVATION_OUTCOME.IDEMPOTENT:
      return t('pages.clientes.deactivation.result.outcome.idempotent');
    case CLIENT_DEACTIVATION_OUTCOME.BLOCKED:
      return t('pages.clientes.deactivation.result.outcome.blocked');
    case CLIENT_DEACTIVATION_OUTCOME.REACTIVATED:
      return t('pages.clientes.deactivation.result.outcome.reactivated');
    default:
      return t('pages.clientes.deactivation.unknownOutcome');
  }
}

function resultStatus(value: ClientDeactivationOutcome): ResultStatus {
  switch (value) {
    case CLIENT_DEACTIVATION_OUTCOME.BLOCKED:
      return RESULT_STATUS.ERROR;
    case CLIENT_DEACTIVATION_OUTCOME.SCHEDULED:
      return RESULT_STATUS.INFO;
    case CLIENT_DEACTIVATION_OUTCOME.IDEMPOTENT:
      return RESULT_STATUS.WARNING;
    case CLIENT_DEACTIVATION_OUTCOME.APPLIED:
    case CLIENT_DEACTIVATION_OUTCOME.REACTIVATED:
      return RESULT_STATUS.SUCCESS;
    default:
      return RESULT_STATUS.INFO;
  }
}

function matchesPreview(
  preview: ClientDeactivationPreviewResponse | undefined,
  input: PreviewClientDeactivationDto,
): preview is ClientDeactivationPreviewResponse {
  if (!preview || preview.operationKey !== input.operationKey || preview.reason !== input.reason) {
    return false;
  }
  if (input.effectiveDate !== undefined && preview.effectiveDate !== input.effectiveDate) {
    return false;
  }
  if (input.cutoffStrategy !== undefined && preview.cutoffStrategy !== input.cutoffStrategy) {
    return false;
  }
  if (
    input.currentPeriodTreatment !== undefined &&
    preview.currentPeriodTreatment !== input.currentPeriodTreatment
  ) {
    return false;
  }
  return true;
}

function buildPreviewInput(
  reason: string,
  operationKey: string,
  selection: ClientDeactivationSelection | null,
  explicitPolicy: boolean,
): PreviewClientDeactivationDto {
  if (!explicitPolicy || selection === null) {
    return { reason, operationKey };
  }
  return {
    reason,
    operationKey,
    cutoffStrategy: selection.cutoffStrategy,
    currentPeriodTreatment: selection.currentPeriodTreatment,
    effectiveDate: selection.effectiveDate,
  };
}

function SectionHeading({ id, children }: { id?: string; children: string }) {
  return (
    <Text id={id} type="secondary" strong className="client-deactivation-modal__section-heading">
      {children}
    </Text>
  );
}

function FinancialMetric({
  label,
  value,
  primary = false,
}: {
  label: string;
  value: ReactNode;
  primary?: boolean;
}) {
  return (
    <div
      className={
        primary
          ? 'client-deactivation-modal__metric client-deactivation-modal__metric--primary'
          : 'client-deactivation-modal__metric'
      }
    >
      <Text type="secondary" className="client-deactivation-modal__metric-label">
        {label}
      </Text>
      <Text strong className="client-deactivation-modal__metric-value">
        {value}
      </Text>
    </div>
  );
}

function CurrentPeriodPreview({
  current,
  t,
}: {
  current: ClientDeactivationCurrentPeriod;
  t: Translate;
}) {
  return (
    <section
      className="client-deactivation-modal__section"
      aria-labelledby="client-deactivation-financial-title"
    >
      <SectionHeading id="client-deactivation-financial-title">
        {t('pages.clientes.deactivation.preview.financialTitle')}
      </SectionHeading>

      <div className="client-deactivation-modal__invoice-row">
        <Text strong>
          {t('pages.clientes.deactivation.preview.invoiceIdValue', { id: current.invoiceId })}
        </Text>
        <Tag>{invoiceStatusLabel(current.invoiceStatus, t)}</Tag>
      </div>

      <div className="client-deactivation-modal__metric-grid client-deactivation-modal__metric-grid--primary">
        <FinancialMetric
          primary
          label={t('pages.clientes.deactivation.preview.grossAmount')}
          value={formatCurrency(current.grossAmount)}
        />
        <FinancialMetric
          primary
          label={t('pages.clientes.deactivation.preview.estimatedServedAmount')}
          value={formatCurrency(current.estimatedServedAmount)}
        />
        <FinancialMetric
          primary
          label={t('pages.clientes.deactivation.preview.estimatedAdjustment')}
          value={formatCurrency(current.estimatedAdjustment)}
        />
      </div>

      <div className="client-deactivation-modal__metric-grid client-deactivation-modal__metric-grid--secondary">
        <FinancialMetric
          label={t('pages.clientes.deactivation.preview.remainder')}
          value={formatCurrency(current.remainder)}
        />
        <FinancialMetric
          label={t('pages.clientes.deactivation.preview.paymentApplied')}
          value={formatCurrency(current.paymentApplied)}
        />
        <FinancialMetric
          label={t('pages.clientes.deactivation.preview.adjustmentsApplied')}
          value={formatCurrency(current.adjustmentsApplied)}
        />
        <FinancialMetric
          label={t('pages.clientes.deactivation.preview.estimatedCredit')}
          value={formatCurrency(current.estimatedCredit)}
        />
      </div>

      <div className="client-deactivation-modal__metadata-grid">
        <div className="client-deactivation-modal__metadata-item">
          <Text type="secondary">{t('pages.clientes.deactivation.preview.period')}</Text>
          <Text>{formatearPeriodo(current.periodStart, current.periodEnd) ?? '—'}</Text>
        </div>
        <div className="client-deactivation-modal__metadata-item">
          <Text type="secondary">{t('pages.clientes.deactivation.preview.dueDate')}</Text>
          <Text>{formatDate(current.dueDate)}</Text>
        </div>
        <div className="client-deactivation-modal__metadata-item">
          <Text type="secondary">{t('pages.clientes.deactivation.preview.servedDays')}</Text>
          <Text>
            {t('pages.clientes.deactivation.preview.servedDaysValue', {
              served: current.servedDays,
              total: current.totalDays,
            })}
          </Text>
        </div>
      </div>
    </section>
  );
}

function FinancialSummaryPreview({
  preview,
  t,
}: {
  preview: ClientDeactivationPreviewResponse;
  t: Translate;
}) {
  return (
    <section
      className="client-deactivation-modal__section"
      aria-labelledby="client-deactivation-debt-title"
    >
      <SectionHeading id="client-deactivation-debt-title">
        {t('pages.clientes.deactivation.preview.debtTitle')}
      </SectionHeading>
      <div className="client-deactivation-modal__summary-grid">
        <div className="client-deactivation-modal__historical-debt">
          <Text type="secondary">{t('pages.clientes.deactivation.preview.historicalDebt')}</Text>
          <Text strong className="client-deactivation-modal__historical-debt-value">
            {formatCurrency(preview.financial.historicalDebtPreserved)}
          </Text>
          <Text type="secondary">
            {t('pages.clientes.deactivation.preview.historicalDebtNote')}
          </Text>
        </div>
        <FinancialMetric
          label={t('pages.clientes.deactivation.preview.futurePrepaidCredit')}
          value={formatCurrency(preview.financial.futurePrepaidCredit)}
          primary
        />
        <FinancialMetric
          label={t('pages.clientes.deactivation.preview.futurePrepaidPeriods')}
          value={String(preview.financial.futurePrepaidPeriods.length)}
        />
      </div>

      {preview.financial.futurePrepaidPeriods.length > 0 ? (
        <Collapse
          size="small"
          bordered={false}
          className="client-deactivation-modal__prepaid-collapse"
          items={[
            {
              key: 'future-prepaid-periods',
              label: t('pages.clientes.deactivation.preview.futurePrepaidPeriodList', {
                count: preview.financial.futurePrepaidPeriods.length,
              }),
              children: (
                <div className="client-deactivation-modal__prepaid-periods">
                  {preview.financial.futurePrepaidPeriods.map((period) => (
                    <div
                      className="client-deactivation-modal__prepaid-period"
                      key={`${period.packageId}-${period.periodId}`}
                    >
                      <Text>{formatearPeriodo(period.periodStart, period.periodEnd) ?? '—'}</Text>
                      <Text strong>{formatCurrency(period.amount)}</Text>
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      ) : null}
    </section>
  );
}

function ServicesImpactPreview({
  preview,
  t,
}: {
  preview: ClientDeactivationPreviewResponse;
  t: Translate;
}) {
  const changing = preview.services.filter((service) => service.willChange);

  return (
    <section
      className="client-deactivation-modal__section"
      aria-labelledby="client-deactivation-services-impact-title"
    >
      <SectionHeading id="client-deactivation-services-impact-title">
        {t('pages.clientes.deactivation.preview.servicesImpactTitle')}
      </SectionHeading>
      {changing.length === 0 ? (
        <Text type="secondary">
          {t('pages.clientes.deactivation.preview.servicesImpactNone')}
        </Text>
      ) : (
        <div>
          <Text>
            {t('pages.clientes.deactivation.preview.servicesImpactCount', {
              changed: changing.length,
              total: preview.services.length,
            })}
          </Text>
          <div className="client-deactivation-modal__services-impact-list">
            {changing.map((service) => (
              <div className="client-deactivation-modal__service-row" key={service.serviceId}>
                <Text strong>
                  {t('pages.clientes.deactivation.preview.serviceId', { id: service.serviceId })}
                </Text>
                <Text type="secondary">{serviceStateLabel(service.currentState, t)}</Text>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function PeriodLiquidationPreview({
  current,
  treatment,
  t,
}: {
  current: ClientDeactivationCurrentPeriod;
  treatment: ClientDeactivationCurrentPeriodTreatment;
  t: Translate;
}) {
  const forgiving =
    treatment === CLIENT_DEACTIVATION_CURRENT_PERIOD_TREATMENT.FORGIVE_CURRENT_PERIOD;

  return (
    <section
      className="client-deactivation-modal__section"
      aria-labelledby="client-deactivation-liquidation-title"
    >
      <SectionHeading id="client-deactivation-liquidation-title">
        {t('pages.clientes.deactivation.preview.liquidationTitle')}
      </SectionHeading>
      <div className="client-deactivation-modal__invoice-row">
        <Text type="secondary">{t('pages.clientes.deactivation.preview.invoiceStatus')}</Text>
        <Tag>{invoiceStatusLabel(current.invoiceStatus, t)}</Tag>
      </div>
      <div className="client-deactivation-modal__metadata-grid">
        <div className="client-deactivation-modal__metadata-item">
          <Text type="secondary">{t('pages.clientes.deactivation.preview.period')}</Text>
          <Text>{formatearPeriodo(current.periodStart, current.periodEnd) ?? '—'}</Text>
        </div>
        <div className="client-deactivation-modal__metadata-item">
          <Text type="secondary">{t('pages.clientes.deactivation.preview.dueDate')}</Text>
          <Text>{formatDate(current.dueDate)}</Text>
        </div>
      </div>
      <div className="client-deactivation-modal__metric-grid client-deactivation-modal__metric-grid--secondary">
        <FinancialMetric
          label={t('pages.clientes.deactivation.preview.estimatedServedAmount')}
          value={formatCurrency(current.estimatedServedAmount)}
        />
        <FinancialMetric
          label={t('pages.clientes.deactivation.preview.estimatedAdjustment')}
          value={formatCurrency(current.estimatedAdjustment)}
        />
        <FinancialMetric
          label={t('pages.clientes.deactivation.preview.estimatedCredit')}
          value={formatCurrency(current.estimatedCredit)}
        />
        <FinancialMetric
          label={t('pages.clientes.deactivation.preview.remainder')}
          value={formatCurrency(current.remainder)}
        />
      </div>
      {forgiving ? (
        <Text type="secondary" className="client-deactivation-modal__liquidation-note">
          {t('pages.clientes.deactivation.treatment.forgiveCurrentPeriodHelp')}
        </Text>
      ) : null}
    </section>
  );
}

function ServiceChangeTag({ willChange, t }: { willChange: boolean; t: Translate }) {
  return (
    <Tag color={willChange ? 'warning' : undefined}>
      {willChange
        ? t('pages.clientes.deactivation.preview.serviceWillChange')
        : t('pages.clientes.deactivation.preview.serviceUnchanged')}
    </Tag>
  );
}

function ServicesPreview({
  preview,
  t,
}: {
  preview: ClientDeactivationPreviewResponse;
  t: Translate;
}) {
  const count = preview.services.length;

  return (
    <section
      className="client-deactivation-modal__section"
      aria-labelledby="client-deactivation-services-title"
    >
      <SectionHeading id="client-deactivation-services-title">
        {t('pages.clientes.deactivation.preview.servicesTitle', { count })}
      </SectionHeading>
      {count === 0 ? (
        <Text type="secondary">{t('pages.clientes.deactivation.preview.noServices')}</Text>
      ) : count === 1 ? (
        <div className="client-deactivation-modal__service-row">
          <Text strong>
            {t('pages.clientes.deactivation.preview.serviceId', {
              id: preview.services[0]?.serviceId,
            })}
          </Text>
          <Text type="secondary">
            {serviceStateLabel(preview.services[0]?.currentState ?? '', t)} →{' '}
            {serviceStateLabel(preview.services[0]?.targetState ?? '', t)}
          </Text>
          <ServiceChangeTag willChange={preview.services[0]?.willChange ?? false} t={t} />
        </div>
      ) : (
        <div className="client-deactivation-modal__service-table-wrapper">
          <table className="client-deactivation-modal__service-table">
            <caption className="client-deactivation-modal__visually-hidden">
              {t('pages.clientes.deactivation.preview.servicesTableCaption')}
            </caption>
            <thead>
              <tr>
                <th scope="col">{t('pages.clientes.deactivation.preview.serviceIdLabel')}</th>
                <th scope="col">{t('pages.clientes.deactivation.preview.currentState')}</th>
                <th scope="col">{t('pages.clientes.deactivation.preview.targetState')}</th>
                <th scope="col">{t('pages.clientes.deactivation.preview.serviceChange')}</th>
              </tr>
            </thead>
            <tbody>
              {preview.services.map((service) => (
                <tr key={service.serviceId}>
                  <th scope="row">
                    {t('pages.clientes.deactivation.preview.serviceId', { id: service.serviceId })}
                  </th>
                  <td>{serviceStateLabel(service.currentState, t)}</td>
                  <td>{serviceStateLabel(service.targetState, t)}</td>
                  <td>
                    <ServiceChangeTag willChange={service.willChange} t={t} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function PreviewDetails({
  preview,
  t,
}: {
  preview: ClientDeactivationPreviewResponse;
  t: Translate;
}) {
  return (
    <>
      {preview.financial.currentPeriod ? (
        <CurrentPeriodPreview current={preview.financial.currentPeriod} t={t} />
      ) : (
        <section
          className="client-deactivation-modal__section"
          aria-labelledby="client-deactivation-financial-title"
        >
          <SectionHeading id="client-deactivation-financial-title">
            {t('pages.clientes.deactivation.preview.financialTitle')}
          </SectionHeading>
          <Alert
            type="info"
            showIcon
            message={t('pages.clientes.deactivation.preview.noCurrentPeriod')}
          />
        </section>
      )}
      <ServicesImpactPreview preview={preview} t={t} />
      {preview.financial.currentPeriod ? (
        <PeriodLiquidationPreview
          current={preview.financial.currentPeriod}
          treatment={preview.currentPeriodTreatment}
          t={t}
        />
      ) : null}
      <FinancialSummaryPreview preview={preview} t={t} />
      <ServicesPreview preview={preview} t={t} />
      <Alert
        className="client-deactivation-modal__final-warning"
        type="warning"
        showIcon
        message={t('pages.clientes.deactivation.preview.finalWarning')}
      />
    </>
  );
}

interface PolicyControlsProps {
  selection: ClientDeactivationSelection;
  currentPeriod: ClientDeactivationCurrentPeriod | null | undefined;
  isBusy: boolean;
  onCutoffStrategyChange: (value: unknown) => void;
  onTreatmentChange: (value: unknown) => void;
  t: Translate;
}

function PolicyControls({
  selection,
  currentPeriod,
  isBusy,
  onCutoffStrategyChange,
  onTreatmentChange,
  t,
}: PolicyControlsProps) {
  const currentPeriodIsPaid =
    currentPeriod?.invoiceStatus === CLIENT_DEACTIVATION_INVOICE_STATUS.PAID;
  const endOfPeriodDisabled = !currentPeriodIsPaid;
  const fullPeriodDisabled = !currentPeriodIsPaid;
  const forgiveCurrentPeriodDisabled =
    !currentPeriod ||
    currentPeriodIsPaid ||
    selection.cutoffStrategy === CLIENT_DEACTIVATION_CUTOFF_STRATEGY.END_OF_PERIOD;
  const chargeServedDaysDisabled =
    selection.cutoffStrategy === CLIENT_DEACTIVATION_CUTOFF_STRATEGY.END_OF_PERIOD;

  return (
    <div className="client-deactivation-modal__policy-groups">
      <fieldset
        className="client-deactivation-modal__policy-group"
        aria-labelledby="client-deactivation-cutoff-label"
      >
        <legend id="client-deactivation-cutoff-label">
          {t('pages.clientes.deactivation.cutoff.label')}
        </legend>
        <Radio.Group
          name="client-deactivation-cutoff"
          aria-labelledby="client-deactivation-cutoff-label"
          value={selection.cutoffStrategy}
          onChange={(event) => onCutoffStrategyChange(event.target.value)}
          disabled={isBusy}
        >
          <div className="client-deactivation-modal__radio-options">
            <Radio
              className="client-deactivation-modal__radio-option"
              value={CLIENT_DEACTIVATION_CUTOFF_STRATEGY.IMMEDIATE}
            >
              <span className="client-deactivation-modal__radio-copy">
                <span className="client-deactivation-modal__radio-copy-title">
                  {t('pages.clientes.deactivation.cutoff.immediate')}
                </span>
                <span className="client-deactivation-modal__radio-copy-help">
                  {t('pages.clientes.deactivation.cutoff.immediateHelp')}
                </span>
              </span>
            </Radio>
            <Radio
              className="client-deactivation-modal__radio-option"
              value={CLIENT_DEACTIVATION_CUTOFF_STRATEGY.END_OF_PERIOD}
              disabled={endOfPeriodDisabled}
            >
              <span className="client-deactivation-modal__radio-copy">
                <span className="client-deactivation-modal__radio-copy-title">
                  {t('pages.clientes.deactivation.cutoff.endOfPeriod')}
                </span>
                <span className="client-deactivation-modal__radio-copy-help">
                  {t('pages.clientes.deactivation.cutoff.endOfPeriodHelp')}
                </span>
              </span>
            </Radio>
          </div>
        </Radio.Group>
      </fieldset>

      <fieldset
        className="client-deactivation-modal__policy-group"
        aria-labelledby="client-deactivation-treatment-label"
      >
        <legend id="client-deactivation-treatment-label">
          {t('pages.clientes.deactivation.treatment.label')}
        </legend>
        <Radio.Group
          name="client-deactivation-treatment"
          aria-labelledby="client-deactivation-treatment-label"
          value={selection.currentPeriodTreatment}
          onChange={(event) => onTreatmentChange(event.target.value)}
          disabled={isBusy}
        >
          <div className="client-deactivation-modal__radio-options">
            <Radio
              className="client-deactivation-modal__radio-option"
              value={CLIENT_DEACTIVATION_CURRENT_PERIOD_TREATMENT.CHARGE_SERVED_DAYS}
              disabled={chargeServedDaysDisabled}
            >
              <span className="client-deactivation-modal__radio-copy">
                <span className="client-deactivation-modal__radio-copy-title">
                  {t('pages.clientes.deactivation.treatment.chargeServedDays')}
                </span>
                <span className="client-deactivation-modal__radio-copy-help">
                  {t('pages.clientes.deactivation.treatment.chargeServedDaysHelp')}
                </span>
              </span>
            </Radio>
            <Radio
              className="client-deactivation-modal__radio-option"
              value={CLIENT_DEACTIVATION_CURRENT_PERIOD_TREATMENT.FULL_PERIOD}
              disabled={fullPeriodDisabled}
            >
              <span className="client-deactivation-modal__radio-copy">
                <span className="client-deactivation-modal__radio-copy-title">
                  {t('pages.clientes.deactivation.treatment.fullPeriod')}
                </span>
                <span className="client-deactivation-modal__radio-copy-help">
                  {t('pages.clientes.deactivation.treatment.fullPeriodHelp')}
                </span>
              </span>
            </Radio>
            <Radio
              className="client-deactivation-modal__radio-option"
              value={CLIENT_DEACTIVATION_CURRENT_PERIOD_TREATMENT.FORGIVE_CURRENT_PERIOD}
              disabled={forgiveCurrentPeriodDisabled}
            >
              <span className="client-deactivation-modal__radio-copy">
                <span className="client-deactivation-modal__radio-copy-title">
                  {t('pages.clientes.deactivation.treatment.forgiveCurrentPeriod')}
                </span>
                <span className="client-deactivation-modal__radio-copy-help">
                  {t('pages.clientes.deactivation.treatment.forgiveCurrentPeriodHelp')}
                </span>
              </span>
            </Radio>
          </div>
        </Radio.Group>
      </fieldset>
    </div>
  );
}

function DeactivationResult({ result, t }: { result: ClientDeactivationResponse; t: Translate }) {
  return (
    <div className="client-deactivation-modal__result">
      <Result
        status={resultStatus(result.outcome)}
        title={t('pages.clientes.deactivation.result.title')}
        subTitle={outcomeLabel(result.outcome, t)}
      />
      <Descriptions
        size="small"
        column={1}
        items={[
          {
            key: 'status',
            label: t('pages.clientes.deactivation.result.statusLabel'),
            children: operationStatusLabel(result.status, t),
          },
          {
            key: 'effectiveDate',
            label: t('pages.clientes.deactivation.effectiveDate'),
            children: formatDate(result.effectiveDate),
          },
          {
            key: 'currentPeriodAdjustment',
            label: t('pages.clientes.deactivation.result.currentPeriodAdjustment'),
            children: formatCurrency(result.currentPeriodAdjustment),
          },
          {
            key: 'currentPeriodCredit',
            label: t('pages.clientes.deactivation.result.currentPeriodCredit'),
            children: formatCurrency(result.currentPeriodCredit),
          },
          {
            key: 'futurePrepaidCredit',
            label: t('pages.clientes.deactivation.result.futurePrepaidCredit'),
            children: formatCurrency(result.futurePrepaidCredit),
          },
          {
            key: 'historicalDebt',
            label: t('pages.clientes.deactivation.result.historicalDebt'),
            children: formatCurrency(result.historicalDebtPreserved),
          },
          {
            key: 'services',
            label: t('pages.clientes.deactivation.result.affectedServices'),
            children: String(result.affectedServiceIds.length),
          },
        ]}
      />
      {result.outcome === CLIENT_DEACTIVATION_OUTCOME.BLOCKED ? (
        <Alert
          type="error"
          showIcon
          message={t('pages.clientes.deactivation.result.blockedMessage')}
        />
      ) : null}
    </div>
  );
}

export function ClientDeactivationModal({
  open,
  cliente,
  onClose,
  onSuccess,
}: ClientDeactivationModalProps) {
  const t = useT();
  const [reason, setReason] = useState('');
  const [selection, setSelection] = useState<ClientDeactivationSelection | null>(null);
  const [explicitPolicy, setExplicitPolicy] = useState(false);
  const [operationKey, setOperationKey] = useState('');
  const [attemptReady, setAttemptReady] = useState(false);
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [previewNeedsRefresh, setPreviewNeedsRefresh] = useState(false);

  const confirmDeactivation = useConfirmClientDeactivation();
  const previewReason = useDebouncedValue(reason.trim());
  const previewInput = buildPreviewInput(previewReason, operationKey, selection, explicitPolicy);
  const previewEnabled =
    open &&
    attemptReady &&
    operationKey.length > 0 &&
    previewReason === reason.trim() &&
    previewReason.length > 0;
  const previewQuery = useClientDeactivationPreview(cliente.id, previewInput, {
    enabled: previewEnabled,
  });
  const rawPreview = previewQuery.data;
  const currentPreview =
    previewReason === reason.trim() && matchesPreview(rawPreview, previewInput) ? rawPreview : null;
  const selectedPolicy =
    selection ??
    (currentPreview
      ? {
          cutoffStrategy: currentPreview.cutoffStrategy,
          currentPeriodTreatment: currentPreview.currentPeriodTreatment,
          effectiveDate: currentPreview.effectiveDate,
        }
      : null);
  const confirmedResult =
    confirmDeactivation.data?.operationKey === operationKey ? confirmDeactivation.data : null;
  const isResult = confirmedResult !== null;
  const isBusy = confirmDeactivation.isPending;
  const currentPeriod = currentPreview?.financial.currentPeriod;
  const canConfirm = Boolean(
    currentPreview &&
    !previewNeedsRefresh &&
    !previewQuery.isFetching &&
    reason.trim().length > 0 &&
    !isBusy,
  );

  useEffect(() => {
    if (!open) {
      setAttemptReady(false);
      return;
    }

    setAttemptReady(false);
    setReason('');
    setSelection(null);
    setExplicitPolicy(false);
    setOperationKey(createOperationKey());
    setReasonError(null);
    setConfirmError(null);
    setPreviewNeedsRefresh(false);
    setAttemptReady(true);
  }, [open]);

  useEffect(() => {
    if (!currentPreview || explicitPolicy) return;

    setSelection((previous) => {
      if (
        previous?.cutoffStrategy === currentPreview.cutoffStrategy &&
        previous.currentPeriodTreatment === currentPreview.currentPeriodTreatment &&
        previous.effectiveDate === currentPreview.effectiveDate
      ) {
        return previous;
      }
      return {
        cutoffStrategy: currentPreview.cutoffStrategy,
        currentPeriodTreatment: currentPreview.currentPeriodTreatment,
        effectiveDate: currentPreview.effectiveDate,
      };
    });
  }, [currentPreview, explicitPolicy]);

  function handleReasonChange(value: string): void {
    setReason(value);
    setReasonError(null);
    setConfirmError(null);
    setPreviewNeedsRefresh(false);
  }

  function handleEffectiveDateChange(value: Dayjs | null): void {
    if (!value || !selectedPolicy) return;
    setExplicitPolicy(true);
    setSelection({ ...selectedPolicy, effectiveDate: value.format('YYYY-MM-DD') });
    setConfirmError(null);
    setPreviewNeedsRefresh(false);
  }

  function handleCutoffStrategyChange(value: unknown): void {
    if (
      value !== CLIENT_DEACTIVATION_CUTOFF_STRATEGY.IMMEDIATE &&
      value !== CLIENT_DEACTIVATION_CUTOFF_STRATEGY.END_OF_PERIOD
    ) {
      return;
    }

    if (!selectedPolicy) return;

    const periodEnd = currentPreview?.financial.currentPeriod?.periodEnd;
    const nextSelection: ClientDeactivationSelection = {
      ...selectedPolicy,
      cutoffStrategy: value,
    };

    if (value === CLIENT_DEACTIVATION_CUTOFF_STRATEGY.END_OF_PERIOD) {
      if (!periodEnd) return;
      nextSelection.effectiveDate = periodEnd;
      nextSelection.currentPeriodTreatment =
        CLIENT_DEACTIVATION_CURRENT_PERIOD_TREATMENT.FULL_PERIOD;
    } else if (
      selectedPolicy.cutoffStrategy === CLIENT_DEACTIVATION_CUTOFF_STRATEGY.END_OF_PERIOD
    ) {
      nextSelection.effectiveDate = TODAY();
      nextSelection.currentPeriodTreatment =
        CLIENT_DEACTIVATION_CURRENT_PERIOD_TREATMENT.CHARGE_SERVED_DAYS;
    }

    setExplicitPolicy(true);
    setSelection(nextSelection);
    setConfirmError(null);
    setPreviewNeedsRefresh(false);
  }

  function handleTreatmentChange(value: unknown): void {
    if (
      value !== CLIENT_DEACTIVATION_CURRENT_PERIOD_TREATMENT.CHARGE_SERVED_DAYS &&
      value !== CLIENT_DEACTIVATION_CURRENT_PERIOD_TREATMENT.FULL_PERIOD &&
      value !== CLIENT_DEACTIVATION_CURRENT_PERIOD_TREATMENT.FORGIVE_CURRENT_PERIOD
    ) {
      return;
    }

    if (!selectedPolicy) return;

    const nextSelection: ClientDeactivationSelection = {
      ...selectedPolicy,
      currentPeriodTreatment: value,
    };
    setConfirmError(null);
    setPreviewNeedsRefresh(false);
    if (value === CLIENT_DEACTIVATION_CURRENT_PERIOD_TREATMENT.FORGIVE_CURRENT_PERIOD) {
      nextSelection.cutoffStrategy = CLIENT_DEACTIVATION_CUTOFF_STRATEGY.IMMEDIATE;
    }
    setExplicitPolicy(true);
    setSelection(nextSelection);
  }

  async function refreshPreview(): Promise<void> {
    setPreviewNeedsRefresh(true);
    const refreshed = await previewQuery.refetch();
    if (refreshed.data && matchesPreview(refreshed.data, previewInput)) {
      setPreviewNeedsRefresh(false);
    }
  }

  async function handleConfirm(): Promise<void> {
    if (!reason.trim()) {
      setReasonError(t('pages.clientes.deactivation.reasonRequired'));
      return;
    }
    if (!currentPreview) {
      setConfirmError(t('pages.clientes.deactivation.previewRequired'));
      return;
    }

    setConfirmError(null);
    try {
      const result = await confirmDeactivation.mutateAsync({
        id: cliente.id,
        dto: {
          reason: reason.trim(),
          cutoffStrategy: currentPreview.cutoffStrategy,
          currentPeriodTreatment: currentPreview.currentPeriodTreatment,
          operationKey: currentPreview.operationKey,
          effectiveDate: currentPreview.effectiveDate,
          previewHash: currentPreview.previewHash,
        },
      });
      onSuccess?.(result);
    } catch (error: unknown) {
      const code = getErrorCode(error);
      if (code === CLIENT_DEACTIVATION_ERROR_CODE.PREVIEW_STALE) {
        setPreviewNeedsRefresh(true);
      }
      setConfirmError(getErrorMessage(error, t));
    }
  }

  const content = confirmedResult ? (
    <DeactivationResult result={confirmedResult} t={t} />
  ) : (
    <div className="client-deactivation-modal__content">
      <Form layout="vertical" requiredMark>
        <section
          className="client-deactivation-modal__section client-deactivation-modal__section--first"
          aria-labelledby="client-deactivation-data-title"
        >
          <SectionHeading id="client-deactivation-data-title">
            {t('pages.clientes.deactivation.preview.dataTitle')}
          </SectionHeading>
          <Form.Item
            label={t('pages.clientes.deactivation.reasonLabel')}
            required
            validateStatus={reasonError ? 'error' : undefined}
          >
            <Input.TextArea
              id="client-deactivation-reason"
              aria-label={t('pages.clientes.deactivation.reasonLabel')}
              aria-describedby={
                reasonError
                  ? 'client-deactivation-reason-meta client-deactivation-reason-error'
                  : 'client-deactivation-reason-meta'
              }
              className="client-deactivation-modal__reason"
              value={reason}
              onChange={(event) => handleReasonChange(event.target.value)}
              placeholder={t('pages.clientes.deactivation.reasonPlaceholder')}
              maxLength={500}
              rows={3}
              disabled={isBusy}
            />
            <div
              id="client-deactivation-reason-meta"
              className="client-deactivation-modal__field-meta"
            >
              <Text type="secondary">{t('pages.clientes.deactivation.reasonHelp')}</Text>
              <Text type="secondary" className="client-deactivation-modal__character-count">
                {t('pages.clientes.deactivation.reasonCharacterCount', { count: reason.length })}
              </Text>
            </div>
            {reasonError ? (
              <div
                id="client-deactivation-reason-error"
                className="client-deactivation-modal__field-error"
                role="alert"
              >
                {reasonError}
              </div>
            ) : null}
          </Form.Item>

          {currentPreview && selectedPolicy ? (
            <Form.Item
              label={t('pages.clientes.deactivation.effectiveDate')}
              extra={
                selectedPolicy.cutoffStrategy ===
                  CLIENT_DEACTIVATION_CUTOFF_STRATEGY.END_OF_PERIOD && currentPeriod
                  ? t('pages.clientes.deactivation.effectiveDateEndOfPeriod', {
                      date: formatDate(currentPeriod.periodEnd),
                    })
                  : t('pages.clientes.deactivation.effectiveDateHelp')
              }
            >
              <DatePicker
                aria-label={t('pages.clientes.deactivation.effectiveDate')}
                value={selectedPolicy.effectiveDate ? dayjs(selectedPolicy.effectiveDate) : null}
                format={DATE_FORMAT}
                allowClear={false}
                disabled={
                  isBusy ||
                  selectedPolicy.cutoffStrategy ===
                    CLIENT_DEACTIVATION_CUTOFF_STRATEGY.END_OF_PERIOD
                }
                onChange={handleEffectiveDateChange}
                style={{ width: '100%' }}
              />
            </Form.Item>
          ) : (
            <div className="client-deactivation-modal__pending-field">
              <Text type="secondary">{t('pages.clientes.deactivation.effectiveDate')}</Text>
              <Text type="secondary">{t('pages.clientes.deactivation.effectiveDatePending')}</Text>
            </div>
          )}
        </section>

        <section
          className="client-deactivation-modal__section"
          aria-labelledby="client-deactivation-policy-title"
        >
          <SectionHeading id="client-deactivation-policy-title">
            {t('pages.clientes.deactivation.preview.policySectionTitle')}
          </SectionHeading>
          {currentPreview && selectedPolicy ? (
            <PolicyControls
              selection={selectedPolicy}
              currentPeriod={currentPeriod}
              isBusy={isBusy}
              onCutoffStrategyChange={handleCutoffStrategyChange}
              onTreatmentChange={handleTreatmentChange}
              t={t}
            />
          ) : (
            <Alert type="info" showIcon message={t('pages.clientes.deactivation.policyWaiting')} />
          )}
        </section>
      </Form>

      {previewNeedsRefresh ? (
        <Alert
          type="warning"
          showIcon
          message={t('pages.clientes.deactivation.previewStale')}
          action={
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={previewQuery.isFetching}
              onClick={() => void refreshPreview()}
            >
              {t('pages.clientes.deactivation.refreshPreview')}
            </Button>
          }
        />
      ) : null}

      {confirmError ? <Alert type="error" showIcon message={confirmError} /> : null}

      <section className="client-deactivation-modal__preview-state">
        {previewQuery.isFetching && !currentPreview ? (
          <Skeleton active paragraph={{ rows: 7 }} title={false} />
        ) : currentPreview ? (
          <PreviewDetails preview={currentPreview} t={t} />
        ) : previewQuery.isError ? (
          <Alert
            type="error"
            showIcon
            message={getErrorMessage(previewQuery.error, t)}
            action={
              <Button size="small" onClick={() => void previewQuery.refetch()}>
                {t('common.retry')}
              </Button>
            }
          />
        ) : previewReason !== reason.trim() ? (
          <Alert type="info" showIcon message={t('pages.clientes.deactivation.preview.updating')} />
        ) : (
          <Alert type="info" showIcon message={t('pages.clientes.deactivation.preview.waiting')} />
        )}
      </section>

      {currentPreview ? (
        <Alert
          className="client-deactivation-modal__confirm-warning"
          type="warning"
          showIcon
          message={t('pages.clientes.deactivation.confirmWarning')}
        />
      ) : null}
    </div>
  );

  return (
    <Modal
      title={
        <div className="client-deactivation-modal__title">
          <span>
            {isResult
              ? t('pages.clientes.deactivation.result.title')
              : t('pages.clientes.deactivation.title')}
          </span>
          <Text type="secondary" className="client-deactivation-modal__title-subtitle">
            {cliente.nombre ?? `#${cliente.id}`}
          </Text>
        </div>
      }
      open={open}
      onCancel={onClose}
      maskClosable={!isBusy}
      keyboard={!isBusy}
      destroyOnHidden
      centered
      width="min(800px, calc(100vw - 24px))"
      rootClassName="client-deactivation-modal"
      classNames={{ body: 'client-deactivation-modal__body' }}
      footer={
        isResult
          ? [
              <Button key="close" type="primary" onClick={onClose}>
                {t('common.close')}
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={onClose} disabled={isBusy}>
                {t('common.cancel')}
              </Button>,
              <Button
                key="confirm"
                type="primary"
                onClick={() => void handleConfirm()}
                loading={isBusy}
                disabled={!canConfirm}
              >
                {t('pages.clientes.deactivation.confirm')}
              </Button>,
            ]
      }
    >
      {content}
    </Modal>
  );
}
